import { useState, useEffect , useCallback} from 'react';

interface ConfigValue {
  min: string;
  max: string;
  steps: string;
}

interface AIModel {
  name: string;
  value: string;
  provider: string;
  is_default: boolean;
  max_length: number;
}


interface AISetting {
  Id: string;
  Name: string;
  ConfigValue: ConfigValue[] | null;
}

type AISettingsResponse = AISetting[];

export const useAISettings = (assistant: { app_id?: string, app_ai_settings?: any, app_chat_settings?: any } = {}) => {
  const [temperature, setTemperature] = useState<number>(0.0);
  const [maximumLength, setMaximumLength] = useState<number>(128000);
  const [stopSequences, setStopSequences] = useState<string>('');
  const [topP, setTopP] = useState<number>(0.5);
  const [fPenalty, setFPenalty] = useState<number>(2.0);
  const [pPenalty, setPPenalty] = useState<number>(2.0);
  const [modal, setModal] = useState<string>('GPT-4o');
  const [aiModels, setAIModels] = useState<any>([]);

  // State for configuration values
  const [temperatureConfig, setTemperatureConfig] = useState<ConfigValue>({ min: "0.0", max: "2.0", steps: "0.1" });
  const [maximumLengthConfig, setMaximumLengthConfig] = useState<ConfigValue>({ min: "0", max: "3000", steps: "1" });
  const [topPConfig, setTopPConfig] = useState<ConfigValue>({ min: "0.0", max: "1.0", steps: "0.1" });
  const [fPenaltyConfig, setFPenaltyConfig] = useState<ConfigValue>({ min: "-2.0", max: "2.0", steps: "0.1" });
  const [pPenaltyConfig, setPPenaltyConfig] = useState<ConfigValue>({ min: "-2.0", max: "2.0", steps: "0.1" });

  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiSettings, setAISettings] = useState<any>(assistant?.app_ai_settings ||  null);
  
  useEffect(() => {
    const getCachedData = (key: string) => {
      const item = sessionStorage.getItem(key);
      if (!item) return null;
      const { value, timestamp } = JSON.parse(item);
      if (Date.now() - timestamp > 2 * 24 * 60 * 60 * 1000) { // 2 days cache
        sessionStorage.removeItem(key);
        return null;
      }
      return value;
    };

    const config = getCachedData('systemConfig');
    if (config) {
      const aiSettings = config.find((item: AISetting) => item.Id === 'ai_settings')?.ConfigValue;
      if (aiSettings && !assistant) {
        setAISettings(aiSettings);
      }
    }
  }, [assistant]);
  
  useEffect(() => {
    if (assistant?.app_chat_settings) {
      // If assistant settings are available, use them
      setTemperature(parseFloat(assistant.app_chat_settings.chat_temperature) || 0.0);
      setMaximumLength(parseInt(assistant.app_chat_settings.chat_maxlength) || 128000);
      const parsedTopP = parseFloat(assistant.app_chat_settings.chat_topP);
      setTopP(isNaN(parsedTopP) ? 0.5 : parsedTopP);
      const parsedFPenalty = parseFloat(assistant.app_chat_settings.chat_frequencypenalty);
      setFPenalty(isNaN(parsedFPenalty) ? 2.0 : parsedFPenalty);
      const parsedPPenalty = parseFloat(assistant.app_chat_settings.chat_presencepenalty);
      setPPenalty(isNaN(parsedPPenalty) ? 2.0 : parsedPPenalty);
      setModal(assistant.app_chat_settings.chat_model || 'GPT-4o');
    } else {
      // Otherwise, fetch from the common/ai-settings endpoint
      const getCachedData = (key: string) => {
        const item = sessionStorage.getItem(key);
        if (!item) return null;
        const { value, timestamp } = JSON.parse(item);
        if (Date.now() - timestamp > 2 * 24 * 60 * 60 * 1000) { // 2 days cache
          sessionStorage.removeItem(key);
          return null;
        }
        return value;
      };
  
      const config = getCachedData('systemConfig');
      if (config) {
        const aiSettings = config.find((item: AISetting) => item.Id === 'ai_settings')?.ConfigValue;
        if (aiSettings) {
          setAISettings(aiSettings);
        }
       
      }
     
    }
  }, [assistant]);

  useEffect(() => {
    if (aiSettings) {
      fetchAISettings();
    }
  }, [aiSettings]); //

  useEffect(() => {
      
  }, [temperatureConfig,temperature]); //

  const fetchAISettings = useCallback(() => {
    if (!aiSettings) return;
    try {
      const parsedResponse: AISettingsResponse = aiSettings;

      const getConfigValue = (id: string): ConfigValue | null => {
        const item = parsedResponse.find((config) => config.Id === id);
        return item && Array.isArray(item.ConfigValue) ? item.ConfigValue[0] : null;
      };

      // const getAllConfigValue = (id: string): ConfigValue[] => {
      //   const item = parsedResponse.find((config) => config.Id === id);
      //   return item && Array.isArray(item.ConfigValue) ? item.ConfigValue : [];
      // };

      const getAllAIModels = (id: string): AIModel[] => {
        const item = parsedResponse.find((config) => config.Id === id);
        return item && Array.isArray(item.ConfigValue)
          ? (item.ConfigValue as unknown as AIModel[])
          : [];
      };

      // Extract and update settings from the API response
      const temperatureCfg = getConfigValue("temperature");
      const maximumLengthCfg = getConfigValue("maximum_length");
      const topPCfg = getConfigValue("topp");
      const fPenaltyCfg = getConfigValue("frequency_penalty");
      const pPenaltyCfg = getConfigValue("presence_penalty");
      const newAiModels = getAllAIModels("model");
      
      if (temperatureCfg) {
        // alert(JSON.stringify(temperatureCfg))
        setTemperature(parseFloat(temperatureCfg.min));
        setTemperatureConfig(temperatureCfg);
      }
      if (maximumLengthCfg) {
        setMaximumLength(parseInt(maximumLengthCfg.max));
        setMaximumLengthConfig(maximumLengthCfg);
      }
      if (topPCfg) {
        setTopP(parseFloat(topPCfg.min));
        setTopPConfig(topPCfg);
      }
      if (fPenaltyCfg) {
        setFPenalty(parseFloat(fPenaltyCfg.min));
        setFPenaltyConfig(fPenaltyCfg);
      }
      if (pPenaltyCfg) {
        setPPenalty(parseFloat(pPenaltyCfg.min));
        setPPenaltyConfig(pPenaltyCfg);
      }
      
      // Add this check before updating aiModels state to prevent unnecessary re-renders
      if (newAiModels && newAiModels.length > 0) {
        // Only update if the models are different
        const currentModelJSON = JSON.stringify(aiModels);
        const newModelJSON = JSON.stringify(newAiModels);
        
        if (currentModelJSON !== newModelJSON) {
          setAIModels(newAiModels);
        }
        // Select the default model automatically
        const defaultModel = newAiModels.find((model: any) => model.is_default);
          if (defaultModel) {
            setModal(defaultModel?.value);
          }
      }

    } catch (err) {
      console.error('Error fetching AI settings', err);
      setError('Failed to fetch AI settings');
    }
  }, [aiSettings]);

  return {
    temperature,
    setTemperature,
    maximumLength,
    setMaximumLength,
    stopSequences,
    setStopSequences,
    topP,
    setTopP,
    fPenalty,
    setFPenalty,
    pPenalty,
    setPPenalty,
    isAIModalOpen,
    setIsAIModalOpen,
    error,
    // Configuration values for sliders
    temperatureConfig,
    maximumLengthConfig,
    topPConfig,
    fPenaltyConfig,
    pPenaltyConfig,
    modal,
    setModal,
    aiSettings,
    setAISettings,
    aiModels
  };
};